import type { DrugAlert } from '../../store/prescriptionsSlice'

interface PrescriptionAlertsProps {
  alerts: DrugAlert[]
}

function getSeverityStyle(severity: string): { bg: string; icon: string; iconColor: string } {
  if (severity === 'Critical') {
    return {
      bg: 'bg-[#f0dede] border-status-error',
      icon: '⛔',
      iconColor: 'text-status-error',
    }
  }
  return {
    bg: 'bg-[#f0e8d0] border-status-warning',
    icon: '⚠️',
    iconColor: 'text-status-warning',
  }
}

export function PrescriptionAlerts({ alerts }: PrescriptionAlertsProps) {
  return (
    <div data-testid="prescription-alerts-section" className="border border-border rounded-[6px] bg-surface">
      <div className="px-4 py-3 border-b border-border">
        <h2 className="text-[14px] font-medium text-text-primary">Alerts</h2>
      </div>
      <div className="p-4">
        {alerts.length === 0 ? (
          <p className="text-[13px] text-text-muted" data-testid="alerts-empty-state">
            No alerts
          </p>
        ) : (
          <div className="flex flex-col gap-2" data-testid="alerts-list">
            {alerts.map((alert) => {
              const style = getSeverityStyle(alert.severity)
              return (
                <div
                  key={alert.id}
                  data-testid="alert-item"
                  className={`flex items-start gap-3 p-3 rounded-[4px] border ${style.bg}`}
                >
                  <span
                    className={`text-[16px] flex-shrink-0 ${style.iconColor}`}
                    data-testid="alert-severity-icon"
                    aria-label={alert.severity}
                  >
                    {style.icon}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span
                        data-testid="alert-severity-badge"
                        className={`inline-flex items-center h-4 px-1 rounded-[2px] text-[10px] font-semibold uppercase ${
                          alert.severity === 'Critical'
                            ? 'bg-status-error text-white'
                            : 'bg-status-warning text-white'
                        }`}
                      >
                        {alert.severity}
                      </span>
                      <span className="text-[12px] font-medium text-text-secondary">
                        {alert.alert_type === 'interaction' ? 'Drug Interaction' : 'Allergy Alert'}
                      </span>
                    </div>
                    <p className="text-[13px] text-text-primary" data-testid="alert-description">
                      {alert.description}
                    </p>
                    {alert.conflicting_drugs && (
                      <p className="text-[12px] text-text-muted mt-0.5" data-testid="alert-conflicting-drugs">
                        Conflicting: {alert.conflicting_drugs}
                      </p>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
